import { useState } from 'react';
import { Upload, FileText, Loader2, Check, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;
const MAX_BYTES = 10 * 1024 * 1024;

/**
 * Lebenslauf nachträglich einspielen (für Personal Brands, die vor dem CV-Onboarding angelegt wurden).
 * Ablauf: Datei → auslesen (nichts gespeichert) → Zusammenfassung → „Übernehmen“ ergänzt das Profil.
 * Eigene Angaben bleiben: leere Felder werden gefüllt, Listen und Stationen ergänzt.
 */
const KIND_LABEL = { pdf: 'PDF', docx: 'DOCX', linkedin_export: 'LinkedIn-Export', other: '' };
const FIELD_LABEL = {
  de: { job: 'Position', company: 'Unternehmen', location: 'Standort', expertise: 'Expertise', cv_summary: 'Über dich', career: 'Werdegang', education: 'Ausbildung', skills: 'Skills', languages: 'Sprachen', industries: 'Branchen', achievements: 'Erfolge', convictions: 'Haltungen', roles: 'Rollen', proof_points: 'Belege' },
  en: { job: 'Position', company: 'Company', location: 'Location', expertise: 'Expertise', cv_summary: 'About you', career: 'Career', education: 'Education', skills: 'Skills', languages: 'Languages', industries: 'Industries', achievements: 'Achievements', convictions: 'Convictions', roles: 'Roles', proof_points: 'Proof points' },
};

// Eine bereits gelesene Datei: Name, Datum, Größe, was daraus gelesen wurde (Zahlen + Kernpunkte), was ins Profil kam.
const SourceCard = ({ src, lang }) => {
  const T = (de, en) => (lang === 'de' ? de : en);
  const [open, setOpen] = useState(false);
  const c = src.counts || {};
  const hl = src.highlights || {};
  const counts = [
    c.career ? T(`${c.career} Stationen`, `${c.career} positions`) : null,
    c.education ? T(`${c.education} Ausbildungen`, `${c.education} education entries`) : null,
    c.skills ? T(`${c.skills} Skills`, `${c.skills} skills`) : null,
    c.achievements ? T(`${c.achievements} Erfolge`, `${c.achievements} achievements`) : null,
    c.roles >= 2 ? T(`${c.roles} parallele Rollen`, `${c.roles} parallel roles`) : null,
    c.posts ? T(`${c.posts} eigene Posts`, `${c.posts} own posts`) : null,
  ].filter(Boolean);
  const sections = [['career', T('Werdegang', 'Career')], ['roles', T('Rollen', 'Roles')], ['education', T('Ausbildung', 'Education')], ['skills', 'Skills'], ['achievements', T('Erfolge', 'Achievements')]].filter(([k]) => (hl[k] || []).length);
  const date = src.uploaded_at ? new Date(src.uploaded_at).toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-GB') : '';
  return (
    <div className="rounded-lg bg-[#1A2B42] p-3" data-testid={`cv-source-${src.id}`}>
      <div className="flex items-start gap-2.5">
        <FileText className="w-5 h-5 text-[#00D9C8] shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-sm text-white truncate" title={src.filename}>{src.filename}</p>
          <p className="text-xs text-[#8696A9]">{[KIND_LABEL[src.kind], date, src.size ? `${Math.max(1, Math.round(src.size / 1024))} KB` : ''].filter(Boolean).join(' · ')}</p>
        </div>
      </div>
      <div className="mt-2 pl-7 space-y-1">
        <p className="text-sm text-[#E6EDF5]">{[src.job, src.company].filter(Boolean).join(' · ') || T('Position nicht erkannt', 'Position not recognised')}</p>
        {counts.length > 0 && <p className="text-xs text-[#A0AEC0]">{T('Gelesen: ', 'Read: ')}{counts.join(' · ')}</p>}
        {(src.changed || []).length > 0 && <p className="text-xs text-[#00D9C8]">{T('Ins Profil übernommen: ', 'Added to profile: ')}{src.changed.map(k => (FIELD_LABEL[lang] || FIELD_LABEL.de)[k] || k).join(', ')}</p>}
        {sections.length > 0 && (
          <button type="button" onClick={() => setOpen(o => !o)} className="text-xs text-[#00D9C8] hover:underline" aria-expanded={open} data-testid={`cv-source-toggle-${src.id}`}>{open ? T('Details ausblenden', 'Hide details') : T('Details anzeigen', 'Show details')}</button>
        )}
        {open && sections.map(([k, label]) => (
          <div key={k} className="pt-1">
            <p className="text-[11px] uppercase tracking-wide text-[#6B7C90]">{label}</p>
            <p className="text-xs text-[#C5D0DD]">{hl[k].join(' · ')}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export const PersonalCvUpdate = ({ lang = 'de', onChanged, sources = [] }) => {
  const { getAuthHeader } = useAuth();
  const T = (de, en) => (lang === 'de' ? de : en);
  const [file, setFile] = useState(null);
  const [found, setFound] = useState(null);
  const [posts, setPosts] = useState([]);       // eigene Posts aus dem LinkedIn-Export
  const [source, setSource] = useState(null);    // Eintrag der gerade ausgelesenen Datei
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const pick = (f) => {
    if (!f) return;
    const ext = (f.name.split('.').pop() || '').toLowerCase();
    if (!['pdf', 'docx', 'zip'].includes(ext)) { setError(T('Bitte PDF, DOCX oder den LinkedIn-Export (ZIP).', 'PDF, DOCX or the LinkedIn export (ZIP).')); return; }
    if (f.size > (ext === 'zip' ? 80 * 1024 * 1024 : MAX_BYTES)) { setError(ext === 'zip' ? T('Die Datei ist größer als 80 MB.', 'The file is larger than 80 MB.') : T('Die Datei ist größer als 10 MB.', 'The file is larger than 10 MB.')); return; }
    setError(''); setFile(f); setFound(null); setPosts([]); setSource(null);
  };

  const extract = async () => {
    setBusy('extract'); setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('language', lang);
      const { data } = await axios.post(`${API}/projects/personal-brand/extract`, form, { headers: getAuthHeader(), timeout: 120000 });
      setFound(data.profile);
      setPosts(data.posts || []);
      setSource(data.source || null);
    } catch (err) {
      setError(err.response?.data?.detail || T('Der Lebenslauf konnte nicht ausgelesen werden.', 'Could not read the CV.'));
    } finally { setBusy(''); }
  };

  const apply = async () => {
    setBusy('apply'); setError('');
    try {
      const { data } = await axios.post(`${API}/projects/active/personal-brand/merge`, { profile: found, source }, { headers: getAuthHeader() });
      let imported = 0;
      if (posts.length) {
        try {
          const r = await axios.post(`${API}/projects/active/voice/samples/import`, { posts, platform: 'linkedin' }, { headers: getAuthHeader() });
          imported = r.data?.imported || 0;
        } catch { /* Posts lassen sich in der Brand DNA ergänzen */ }
      }
      if (imported) toast.success(T(`${imported} eigene Posts als Stil-Vorbild übernommen`, `${imported} of your own posts added as style examples`));
      toast.success(data.changed?.length
        ? T('Profil ergänzt. Bitte prüfe die Strategie und erstelle sie bei Bedarf neu.', 'Profile updated. Check your strategy and regenerate it if needed.')
        : T('Es gab nichts Neues zu ergänzen.', 'Nothing new to add.'));
      setFile(null); setFound(null); setPosts([]); setSource(null);
      onChanged && onChanged();
    } catch (err) {
      setError(err.response?.data?.detail || T('Das Profil konnte nicht ergänzt werden.', 'Could not update the profile.'));
    } finally { setBusy(''); }
  };

  const counts = found ? [
    found.career?.length ? T(`${found.career.length} Stationen`, `${found.career.length} positions`) : null,
    found.education?.length ? T(`${found.education.length} Ausbildungen`, `${found.education.length} education entries`) : null,
    found.skills?.length ? T(`${found.skills.length} Skills`, `${found.skills.length} skills`) : null,
    found.achievements?.length ? T(`${found.achievements.length} Erfolge`, `${found.achievements.length} achievements`) : null,
    found.roles?.length >= 2 ? T(`${found.roles.length} parallele Rollen`, `${found.roles.length} parallel roles`) : null,
  ].filter(Boolean).join(' · ') : '';

  return (
    <div className="space-y-2" data-testid="cv-update">
      <div className="space-y-2 mb-3" data-testid="cv-sources">
        <p className="text-[11px] uppercase tracking-wide text-[#6B7C90]">{T('Bisher gelesen', 'Read so far')}</p>
        {sources.length > 0
          ? [...sources].reverse().map(src => <SourceCard key={src.id} src={src} lang={lang} />)
          : <p className="text-xs text-[#6B7C90]">{T('Noch keine Datei gelesen. Lade unten deinen Lebenslauf oder LinkedIn-Export hoch.', 'No file read yet. Upload your CV or LinkedIn export below.')}</p>}
        {sources.length > 0 && <p className="text-[11px] text-[#6B7C90]">{T('Die Dateien selbst werden nicht gespeichert, nur das Ergebnis.', 'The files themselves are not stored, only the result.')}</p>}
      </div>
      <p className="text-[11px] uppercase tracking-wide text-[#6B7C90]">{T('Weitere Datei hochladen', 'Upload another file')}</p>
      <p className="text-xs text-[#8696A9]">{T('Lade deinen Lebenslauf (PDF oder DOCX) oder den kompletten LinkedIn-Export (ZIP, enthält auch deine Posts) hoch. Deine bisherigen Angaben bleiben, wir ergänzen nur Fehlendes. Die Datei wird nicht gespeichert.', 'Upload your CV (PDF or DOCX) or the full LinkedIn export (ZIP, includes your posts). Your existing details stay; we only add what is missing. The file is not stored.')}</p>
      <label className={`flex items-center gap-3 rounded-lg border border-dashed px-4 py-3 cursor-pointer transition-colors ${file ? 'border-[#00D9C8] bg-[#00D9C8]/10' : 'border-[#2A3B52] bg-[#1A2B42] hover:border-[#00D9C8]/50'}`}>
        {file ? <FileText className="w-5 h-5 text-[#00D9C8] shrink-0" /> : <Upload className="w-5 h-5 text-[#A0AEC0] shrink-0" />}
        <span className="flex-1 min-w-0 text-sm text-white truncate">{file ? file.name : T('PDF, DOCX oder LinkedIn-Export (ZIP) auswählen', 'Choose a PDF, DOCX or LinkedIn export (ZIP)')}</span>
        {file && <button type="button" onClick={e => { e.preventDefault(); setFile(null); setFound(null); setPosts([]); setSource(null); }} className="text-[#5A6C7D] hover:text-red-400 p-1" aria-label="Remove"><X className="w-4 h-4" /></button>}
        <input type="file" className="sr-only" accept=".pdf,.docx,.zip,application/pdf,application/zip,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={e => { pick(e.target.files?.[0]); e.target.value = ''; }} data-testid="cv-update-input" />
      </label>

      {found && (
        <div className="rounded-lg border border-[#00D9C8]/40 bg-[#00D9C8]/5 p-3" data-testid="cv-update-found">
          <p className="text-xs text-[#00D9C8] mb-1 flex items-center gap-1"><Check className="w-3.5 h-3.5" />{T('Das haben wir gefunden', 'What we found')}</p>
          <p className="text-sm text-white">{[found.job, found.company].filter(Boolean).join(' · ') || T('Position nicht erkannt', 'Position not recognised')}</p>
          {counts && <p className="text-xs text-[#A0AEC0] mt-1">{counts}</p>}
          {posts.length > 0 && <p className="text-xs text-[#00D9C8] mt-1" data-testid="cv-update-posts">{T(`${posts.length} eigene Posts gefunden`, `${posts.length} of your own posts found`)}</p>}
        </div>
      )}
      {error && <p className="text-red-400 text-xs" data-testid="cv-update-error">{error}</p>}

      {found ? (
        <Button size="sm" onClick={apply} disabled={!!busy} className="bg-[#00D9C8] hover:bg-[#00C4B4] text-[#0A1628]" data-testid="cv-update-apply">
          {busy === 'apply' && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}{T('In Profil übernehmen', 'Add to profile')}
        </Button>
      ) : (
        <Button size="sm" onClick={extract} disabled={!file || !!busy} className="bg-[#00D9C8] hover:bg-[#00C4B4] text-[#0A1628]" data-testid="cv-update-extract">
          {busy === 'extract' && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}{busy === 'extract' ? T('Wird gelesen …', 'Reading …') : T('Auslesen', 'Read')}
        </Button>
      )}
    </div>
  );
};

export default PersonalCvUpdate;
